import { useContext, useEffect, useRef, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { TieredMenu } from "primereact/tieredmenu";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_ADMIN, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { AI_MESSAGE_FOR_COMPLIANCE, getErrorTitle, RESIDENT_CARE_PLAN_TYPE, USER_GROUP_ADMIN } from "../../../constants/constants";
import { openUrlInNewTab } from "../../../utils/securityUtils";
import {
    dashboardPageContainerStyle,
    renderDashboardHeader,
} from "../../dashboard/pages/dashboardStyles";
import {
    appAccentPanelStyle,
    appDialogContentStyle,
    appDialogHeaderStyle,
    appDialogStyle,
    appSectionCardStyle,
    renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import { DELEGATION_FORMS } from "./delegationFormRegistry";
import { getDelegationTemplateDefinition } from "./delegationFormTemplates";
import {
    DELEGATION_FORM_STATUS,
    getDelegationStatusLabel,
    getDelegationStatusSeverity,
    getDelegationStatusTagValue,
    isDelegationCompletedStatus,
    normalizeDelegationStatus,
} from "./delegationStatus";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import DelegationStaffManagementDialog from "../components/DelegationStaffManagementDialog";
import DelegationTaskManagementDialog from "../components/DelegationTaskManagementDialog";

const getDelegationThreadId = (form: any) => String(form?.formThreadId || "").trim() || "default";
const getDelegationThreadLabel = (form: any) => String(form?.formThreadLabel || "").trim();
const getDelegationStaffId = (staff: any) => String(staff?.staffId || staff?.caregiverId || staff?.userId || "").trim();
const normalizeDelegationTitleToken = (value: any) => String(value || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
const getDelegationStaffKey = (form: any) => {
    const staffIds = Array.isArray(form?.selectedStaffIds)
        ? form.selectedStaffIds.map((staffId: any) => String(staffId || "").trim()).filter(Boolean).sort()
        : [];
    if (staffIds.length) return `id:${staffIds.join(",")}`;

    const staffNames = Array.isArray(form?.selectedStaff)
        ? form.selectedStaff.map((staff: any) => normalizeDelegationTitleToken(staff?.name)).filter(Boolean).sort()
        : [];
    return staffNames.length ? `name:${staffNames.join(",")}` : "general";
};
const getDelegationTaskKey = (formCode: string, form: any) => {
    if (String(formCode || "").trim().toUpperCase() !== "13-678") {
        return "general";
    }
    const taskId = String(form?.taskId || "").trim();
    if (taskId) return `id:${taskId}`;
    const taskName = normalizeDelegationTitleToken(form?.taskName);
    return taskName ? `name:${taskName}` : "general";
};
const getDelegationThreadKeyForForm = (formCode: string, form: any) =>
    `${String(formCode || "").trim().toUpperCase()}::${getDelegationThreadId(form)}::${getDelegationStaffKey(form)}::${getDelegationTaskKey(formCode, form)}`;
const isStaffSpecificDelegationForm = (formCode: any) => ["10-217", "14-484"].includes(String(formCode || "").trim().toUpperCase());
const hasStaffDelegationRequirements = (staff: any) => [
    staff?.dateOfBirth,
    staff?.credentialType,
    staff?.credentialNumber,
    staff?.credentialIssueDate,
    staff?.credentialExpiryDate,
    staff?.coreTrainingCompleted === true ? true : staff?.coreTrainingDate,
    staff?.diabetesTrainingCompleted === true ? true : staff?.diabetesTrainingDate,
    staff?.narBasicTrainingCompleted === true ? true : staff?.narBasicTrainingDate,
    staff?.basicTrainingCoreCompetencyCompleted === true ? true : staff?.basicTrainingCoreCompetencyDate,
    staff?.basicTraining75HoursCompleted === true ? true : staff?.basicTraining75HoursDate,
    staff?.fundamentalsOfCareCompleted === true ? true : staff?.fundamentalsOfCareDate,
    staff?.revisedFundamentalsOfCareCompleted === true ? true : staff?.revisedFundamentalsOfCareDate,
    staff?.dda32HourLetterCompleted === true ? true : staff?.dda32HourLetterDate,
    staff?.exemptLtcwCompleted === true ? true : staff?.exemptLtcwDate,
].some((value) => value === true || String(value || "").trim() !== "");
const getDelegationDisplayTitle = (formConfig: any, form: any, residentName?: any) => {
    let storedTitle = String(form?.formDisplayTitle || form?.formTitle || formConfig?.title || "Delegation Form")
        .trim()
        .replace(/^Nurse Delegation:\s*/i, "");
    const staffName = formConfig?.id === "10-217" ? String(form?.selectedStaff?.[0]?.name || "").trim() : "";
    const normalizedResidentName = String(residentName || "").trim();
    const residentSuffixes = [
        normalizedResidentName ? `${normalizedResidentName} (Resident)` : "",
        normalizedResidentName,
    ].filter(Boolean);
    for (const suffix of residentSuffixes) {
        const suffixToken = ` - ${suffix}`;
        if (storedTitle.toLowerCase().endsWith(suffixToken.toLowerCase())) {
            storedTitle = storedTitle.slice(0, -suffixToken.length).trim();
            break;
        }
    }
    storedTitle = storedTitle.replace(/\s+-\s+[^-]+ \(Resident\)$/i, "").trim();
    let titleParts = storedTitle.split(/\s+-\s+/).filter(Boolean);
    const staffToken = normalizeDelegationTitleToken(staffName);
    while (titleParts.length > 1 && staffToken) {
        const lastTitlePart = titleParts[titleParts.length - 1];
        const normalizedLastPart = normalizeDelegationTitleToken(lastTitlePart);
        if (normalizedLastPart !== staffToken && normalizedLastPart !== `${staffToken}staff`) break;
        titleParts = titleParts.slice(0, -1);
    }
    const cleanedTitle = titleParts.join(" - ");
    const titleWithStaff = staffName ? `${cleanedTitle} - ${staffName}` : cleanedTitle;
    const threadLabel = getDelegationThreadLabel(form);
    return threadLabel && !normalizeDelegationTitleToken(titleWithStaff).includes(normalizeDelegationTitleToken(threadLabel))
        ? `${titleWithStaff} - ${threadLabel}`
        : titleWithStaff;
};
const MEDICATION_LIST_SPLIT_REGEX = /\r?\n|;|â€¢|Â·/g;
const MEDICATION_NUMBERING_REGEX = /\s+\d+\.\s+/g;
const DOSAGE_HINT_REGEX = /\b(mg|mcg|g|ml|units?|tablet|tablets|tab|tabs|capsule|capsules|cap|caps|poq|po|bid|tid|qid|qhs|hs|prn|daily|weekly)\b/i;

const extractMedicationNamesFromText = (value: any): string[] => {
    const cleanedValue = String(value || "")
        .replace(MEDICATION_NUMBERING_REGEX, "\n")
        .replace(MEDICATION_LIST_SPLIT_REGEX, "\n")
        .replace(/\t/g, " ")
        .trim();

    if (!cleanedValue) {
        return [];
    }

    const initialLines = cleanedValue
        .split(/\r?\n/)
        .map((line) => line.replace(/^[\-\u2022\s]+/, "").trim())
        .filter(Boolean);

    const expandedLines = initialLines.flatMap((line) => {
        if (line.includes(",") && !DOSAGE_HINT_REGEX.test(line)) {
            const commaParts = line.split(",").map((part) => part.trim()).filter(Boolean);
            if (commaParts.length > 1) {
                return commaParts;
            }
        }
        return [line];
    });

    return Array.from(new Set(expandedLines));
};

const normalizeMedicationSourceEntries = (items: any[]) => (
    (Array.isArray(items) ? items : [])
        .map((item: any) => ({
            medicationName: String(item?.medicationName || item?.name || "").trim(),
            dosage: String(item?.dosage || item?.dose || "").trim(),
            route: String(item?.route || "").trim(),
            indication: String(item?.indication || item?.reason || "").trim(),
            instructions: String(item?.instructions || item?.handlingNotes || item?.notes || "").trim(),
            prn: item?.prn === true || String(item?.scheduleType || "").trim().toLowerCase() === "prn",
        }))
        .filter((item) => item.medicationName)
);

const getMedicationEntriesFromCarePlanSection = (medicationSection: any) => {
    const normalizedEntries = normalizeMedicationSourceEntries(medicationSection?.medicationEntries || []);
    if (normalizedEntries.length) {
        return normalizedEntries;
    }

    return extractMedicationNamesFromText(
        medicationSection?.assessmentMedicationListRawText
        || medicationSection?.medicationAmountReasonableDetails
        || "",
    ).map((medicationName) => ({
        medicationName,
        dosage: "",
        route: "",
        indication: "",
        instructions: "",
        prn: false,
    }));
};

const NurseResidentDelegationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { residentId, homeId } = useParams();
    const { authStore, backendAPIStore } = useContext(MobXProviderContext);
    const toast = useRef<Toast>(null);
    const { completeStep, isStepActive } = useTour();

    const [loadingProfile, setLoadingProfile] = useState(true);
    const [residentProfile, setResidentProfile] = useState<any | null>(null);
    const [selectedFormId, setSelectedFormId] = useState("01-212");
    const [residentForms, setResidentForms] = useState<any[]>([]);
    const [savedDraftSummary, setSavedDraftSummary] = useState<Record<string, { count: number; updatedAt?: string }>>({});
    const [showVersionsDialog, setShowVersionsDialog] = useState(false);
    const [showStaffSelectionDialog, setShowStaffSelectionDialog] = useState(false);
    const [staffSelectionAction, setStaffSelectionAction] = useState<"manual" | "assessment" | "ncp" | "">("");
    const [staffOptions, setStaffOptions] = useState<any[]>([]);
    const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
    const [loadingStaffOptions, setLoadingStaffOptions] = useState(false);
    const [showManageStaffDialog, setShowManageStaffDialog] = useState(false);
    const [showDelegationTaskDialog, setShowDelegationTaskDialog] = useState(false);
    const [delegationTaskSelectionMode, setDelegationTaskSelectionMode] = useState(false);
    const [pendingDelegationTaskForm, setPendingDelegationTaskForm] = useState<typeof DELEGATION_FORMS[number] | null>(null);
    const [staffEditId, setStaffEditId] = useState("");
    const [creatingThreadForm, setCreatingThreadForm] = useState(false);
    const [selectedVersionThreadKey, setSelectedVersionThreadKey] = useState("");
    const [highlightedDelegationFormIds, setHighlightedDelegationFormIds] = useState<string[]>([]);
    const delegationFormRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const delegationFormMenuRef = useRef<TieredMenu>(null);
    const [importingMedicationSource, setImportingMedicationSource] = useState(false);
    const [showAssessmentVersionDialog, setShowAssessmentVersionDialog] = useState(false);
    const [loadingAssessmentVersions, setLoadingAssessmentVersions] = useState(false);
    const [assessmentVersionOptions, setAssessmentVersionOptions] = useState<any[]>([]);
    const [currentAssessmentVersionFormId, setCurrentAssessmentVersionFormId] = useState("");
    const [selectedAssessmentVersionFormId, setSelectedAssessmentVersionFormId] = useState("");
    const [showNcpVersionDialog, setShowNcpVersionDialog] = useState(false);
    const [loadingNcpVersions, setLoadingNcpVersions] = useState(false);
    const [ncpVersionOptions, setNcpVersionOptions] = useState<any[]>([]);
    const [currentNcpVersionFormId, setCurrentNcpVersionFormId] = useState("");
    const [selectedNcpVersionFormId, setSelectedNcpVersionFormId] = useState("");
    const [showMedicationSourcePreviewDialog, setShowMedicationSourcePreviewDialog] = useState(false);
    const [medicationSourcePreviewTitle, setMedicationSourcePreviewTitle] = useState("Review Medication Source");
    const [medicationSourcePreviewEntries, setMedicationSourcePreviewEntries] = useState<any[]>([]);
    const [medicationSourceType, setMedicationSourceType] = useState<"carePlan" | "ncp" | "">("");
    const [medicationSourceDiagnosisText, setMedicationSourceDiagnosisText] = useState("");
    const [medicationSourcePreviewWarnings, setMedicationSourcePreviewWarnings] = useState<string[]>([]);
    const [missingMedicationSourceLabel, setMissingMedicationSourceLabel] = useState("this medication source");
    const [delegationDraftJobId, setDelegationDraftJobId] = useState("");
    const [delegationDraftJobStatus, setDelegationDraftJobStatus] = useState("");
    const [delegationDraftJobSubStatus, setDelegationDraftJobSubStatus] = useState("");
    const [delegationDraftJobDialogVisible, setDelegationDraftJobDialogVisible] = useState(false);
    const [delegationDraftElapsedSeconds, setDelegationDraftElapsedSeconds] = useState(0);
    const [delegationDraftGeneratedForms, setDelegationDraftGeneratedForms] = useState<any[]>([]);
    const [delegationDraftResultMessage, setDelegationDraftResultMessage] = useState("");
    const [delegationDraftErrorMessage, setDelegationDraftErrorMessage] = useState("");
    const [showCarePlanRequiredDialog, setShowCarePlanRequiredDialog] = useState(false);
    const [showDelegationDraftReuseDialog, setShowDelegationDraftReuseDialog] = useState(false);
    const [delegationDraftReuseForms, setDelegationDraftReuseForms] = useState<any[]>([]);
    const [delegationDraftReuseSourceType, setDelegationDraftReuseSourceType] = useState<"carePlan" | "ncp" | "">("");
    const [delegationDraftReuseOptions, setDelegationDraftReuseOptions] = useState<{
        sourceLabel?: string;
        assessmentFormId?: string;
        ncpFormId?: string;
    } | null>(null);
    const [showFeatureAccessDialog, setShowFeatureAccessDialog] = useState(false);
    const [featureAccessDialogMessage, setFeatureAccessDialogMessage] = useState("");
    const [featureAccessDialogAllowsCreditUse, setFeatureAccessDialogAllowsCreditUse] = useState(false);
    const [automationCreditsAvailable, setAutomationCreditsAvailable] = useState(0);
    const delegationFeatureAccessRetryActionRef = useRef<(() => void) | null>(null);

    const userRoleType = authStore.getUserRoleType();
    const userGroups = authStore.getUserGroups() || [];
    const isAdminUser = userGroups.includes(USER_GROUP_ADMIN) || userRoleType === ROLE_TYPE_ADMIN;
    const isMobile = useResponsive().isMobile;
    const isProviderNurseResidentMode = location.pathname.startsWith("/provider/nurse/");
    const isSharedResidentMode = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/delegation/");
    const isProviderResidentMode = location.pathname.startsWith("/home/") && location.pathname.includes("/delegation/");
    const requiresProviderSubscriptionMessageOnly = isSharedResidentMode;
    const usesProviderDelegationApi = isProviderResidentMode || isProviderNurseResidentMode || isSharedResidentMode;
    const usesProviderCarePlanSource = (isProviderResidentMode || isSharedResidentMode) && !isProviderNurseResidentMode;

    const applyDelegationFormsResponse = (delegationResponse: any) => {
        const forms = delegationResponse?.forms || [];
        setResidentForms(forms);
        const formSummary = forms.reduce((acc: Record<string, { count: number; updatedAt?: string }>, form: any) => {
            const formCode = String(form?.formCode || "").trim().toUpperCase();
            if (!formCode) return acc;
            const threadKey = getDelegationThreadKeyForForm(formCode, form);
            const existing = acc[threadKey] || { count: 0, updatedAt: "" };
            const nextUpdatedAt = existing.updatedAt && String(existing.updatedAt).localeCompare(String(form?.updatedAt || form?.createdAt || "")) > 0
                ? existing.updatedAt
                : (form?.updatedAt || form?.createdAt || "");
            acc[threadKey] = {
                count: existing.count + 1,
                updatedAt: nextUpdatedAt,
            };
            return acc;
        }, {});
        setSavedDraftSummary(formSummary);
    };

    const refreshDelegationForms = async () => {
        if (!residentId) return;
        const delegationResponse: any = usesProviderDelegationApi
            ? await backendAPIStore.getProviderDelegationForms(residentId)
            : await backendAPIStore.getNurseDelegationForms(residentId);
        applyDelegationFormsResponse(delegationResponse);
    };

    useEffect(() => {
        const loadResidentProfile = async () => {
            if (!residentId) return;
            setLoadingProfile(true);
            try {
                await backendAPIStore.init();
                const response = (isProviderResidentMode || isSharedResidentMode)
                    ? await backendAPIStore.getProviderResidentProfile(residentId)
                    : await backendAPIStore.getNurseResidentProfile(residentId);
                setResidentProfile(response || null);
                await refreshDelegationForms();
            } catch (error: any) {
                toast.current?.show({
                    severity: "error",
                    summary: getErrorTitle(error),
                    detail: error?.message || "Failed to load resident profile.",
                    life: 4000,
                });
            } finally {
                setLoadingProfile(false);
            }
        };
        void loadResidentProfile();
    }, [residentId, backendAPIStore, isProviderResidentMode, isSharedResidentMode, usesProviderDelegationApi]);

    useEffect(() => {
        if (!delegationDraftJobId || !delegationDraftJobDialogVisible) {
            return;
        }

        let cancelled = false;

        const pollDelegationDraftJob = async () => {
            for (let attempt = 0; attempt < 90; attempt += 1) {
                if (cancelled) {
                    return;
                }

                try {
                    const jobDetails: any = await backendAPIStore.getJobDetails(delegationDraftJobId);
                    const nextStatus = String(jobDetails?.jobStatus || "").trim();
                    const nextSubStatus = String(jobDetails?.jobSubStatus || "").trim();
                    if (!cancelled) {
                        setDelegationDraftJobStatus(nextStatus);
                        setDelegationDraftJobSubStatus(nextSubStatus);
                    }

                    if (nextStatus.toUpperCase() === "COMPLETED") {
                        const createdForms = Array.isArray(jobDetails?.generatedDelegationForms) ? jobDetails.generatedDelegationForms : [];
                        if (!cancelled) {
                            await refreshDelegationForms();
                            setDelegationDraftGeneratedForms(createdForms);
                            setDelegationDraftResultMessage(String(jobDetails?.resultMessage || "").trim());
                            if (!createdForms.length) {
                                setDelegationDraftErrorMessage("");
                                return;
                            }
                            setImportingMedicationSource(false);
                            setShowAssessmentVersionDialog(false);
                            setShowNcpVersionDialog(false);
                            setShowMedicationSourcePreviewDialog(false);
                            setMedicationSourcePreviewTitle("Review Medication Source");
                            setMedicationSourcePreviewEntries([]);
                            setMedicationSourceType("");
                            setMedicationSourceDiagnosisText("");
                            setMedicationSourcePreviewWarnings([]);
                            setDelegationDraftJobId("");
                            setDelegationDraftElapsedSeconds(0);
                            setDelegationDraftJobDialogVisible(true);
                            setDelegationDraftErrorMessage("");
                        }
                        return;
                    }

                    if (nextStatus.toUpperCase() === "FAILED") {
                        if (!cancelled) {
                            setDelegationDraftGeneratedForms([]);
                            setDelegationDraftResultMessage("");
                            setDelegationDraftErrorMessage(String(jobDetails?.errorMessage || "Unable to generate delegation draft forms from this source."));
                        }
                        return;
                    }
                } catch (error: any) {
                    if (!cancelled) {
                        setDelegationDraftGeneratedForms([]);
                        setDelegationDraftResultMessage("");
                        setDelegationDraftErrorMessage(error?.message || "Unable to check delegation draft progress.");
                    }
                    return;
                }

                await new Promise((resolve) => window.setTimeout(resolve, 2000));
            }
        };

        void pollDelegationDraftJob();

        return () => {
            cancelled = true;
        };
    }, [backendAPIStore, delegationDraftJobDialogVisible, delegationDraftJobId]);

    useEffect(() => {
        if (!delegationDraftJobDialogVisible) {
            setDelegationDraftElapsedSeconds(0);
            return;
        }

        const startTime = Date.now();
        setDelegationDraftElapsedSeconds(0);
        const timer = window.setInterval(() => {
            setDelegationDraftElapsedSeconds(Math.round((Date.now() - startTime) / 1000));
        }, 1000);

        return () => {
            window.clearInterval(timer);
        };
    }, [delegationDraftJobDialogVisible, delegationDraftJobId]);

    if (userRoleType !== ROLE_TYPE_NURSE && userRoleType !== ROLE_TYPE_MHP && userRoleType !== ROLE_TYPE_PROVIDER && !isAdminUser) {
        return <Unauthorized />;
    }

    const selectedForm = DELEGATION_FORMS.find((form) => form.id === selectedFormId) || null;
    const residentDisplayName = `${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim();
    const getFormVersions = (formCode: string, formThreadId?: string, staffKey?: string) => residentForms
        .filter((form: any) =>
            String(form?.formCode || "").trim().toUpperCase() === String(formCode || "").trim().toUpperCase()
            && getDelegationThreadId(form) === (String(formThreadId || "").trim() || "default")
            && (!staffKey || getDelegationStaffKey(form) === staffKey)
        )
        .sort((a: any, b: any) => (b?.version || 0) - (a?.version || 0));
    const getResidentDraftForForm = (formCode: string, formThreadId?: string, staffKey?: string) => {
        const versions = getFormVersions(formCode, formThreadId, staffKey);
        return versions.find((form: any) => normalizeDelegationStatus(form?.status) === DELEGATION_FORM_STATUS.DRAFT) || null;
    };
    const getCompletedVersionForForm = (formCode: string, formThreadId?: string, staffKey?: string) => getFormVersions(formCode, formThreadId, staffKey)
        .find((form: any) => isDelegationCompletedStatus(form?.status)) || null;
    const getThreadSummariesForForm = (formCode: string) => {
        const threadMap = new Map<string, any[]>();
        residentForms
            .filter((form: any) => String(form?.formCode || "").trim().toUpperCase() === String(formCode || "").trim().toUpperCase())
            .forEach((form: any) => {
                const threadKey = getDelegationThreadKeyForForm(formCode, form);
                const existing = threadMap.get(threadKey) || [];
                existing.push(form);
                threadMap.set(threadKey, existing);
            });

        return Array.from(threadMap.entries())
            .map(([threadKey, forms]) => {
                const sortedForms = [...forms].sort((a: any, b: any) => (b?.version || 0) - (a?.version || 0));
                const latestForm = sortedForms[0] || null;
                const threadId = getDelegationThreadId(latestForm);
                const staffKey = getDelegationStaffKey(latestForm);
                return {
                    threadKey,
                    threadId,
                    threadLabel: getDelegationThreadLabel(latestForm),
                    latestForm,
                    forms: sortedForms,
                    count: sortedForms.length,
                    updatedAt: latestForm?.updatedAt || latestForm?.createdAt || "",
                    residentDraft: getResidentDraftForForm(formCode, threadId, staffKey),
                    completedVersion: getCompletedVersionForForm(formCode, threadId, staffKey),
                };
            })
            .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
    };
    const savedFormThreads = residentForms
        .reduce((acc: any[], form: any) => {
            const formCode = String(form?.formCode || "").trim().toUpperCase();
            const formConfig = DELEGATION_FORMS.find((item) => item.id === formCode);
            if (!formConfig) return acc;
            const threadKey = getDelegationThreadKeyForForm(formCode, form);
            if (acc.some((item) => item.threadKey === threadKey)) {
                return acc;
            }
            const threadSummary = getThreadSummariesForForm(formCode).find((item) => item.threadKey === threadKey);
            if (!threadSummary) return acc;
            acc.push({
                ...threadSummary,
                formCode,
                formConfig,
            });
            return acc;
        }, [])
        .sort((a, b) => String(b.updatedAt || "").localeCompare(String(a.updatedAt || "")));
    const selectedFormThreads = selectedForm ? getThreadSummariesForForm(selectedForm.id) : [];
    const activeVersionThreadKey = selectedVersionThreadKey || selectedFormThreads[0]?.threadKey || "";
    const selectedThreadSummary = selectedFormThreads.find((thread) => thread.threadKey === activeVersionThreadKey) || selectedFormThreads[0] || null;
    const selectedFormVersions = selectedThreadSummary?.forms || [];
    const selectedResidentDraft = selectedThreadSummary?.residentDraft || null;
    const selectedCompletedVersion = selectedThreadSummary?.completedVersion || null;
    const hasVersionHistory = selectedFormVersions.length > 1;

    const openDelegationForm = (formCode: string, formId?: string) => {
        if (!residentId) return;
        navigate(
            isSharedResidentMode
                ? RouterAddress.getSharedDelegationFormPagePath(homeId || "", residentId, formCode, formId)
                : isProviderResidentMode && homeId
                    ? RouterAddress.getProviderDelegationFormPagePath(homeId, residentId, formCode, formId)
                    : isProviderNurseResidentMode
                        ? RouterAddress.getProviderNurseDelegationFormPagePath(residentId, formCode, formId)
                        : RouterAddress.getNurseDelegationFormPagePath(residentId, formCode, formId)
        );
    };

    const resetMedicationSourceState = () => {
        setImportingMedicationSource(false);
        setShowAssessmentVersionDialog(false);
        setShowNcpVersionDialog(false);
        setShowMedicationSourcePreviewDialog(false);
        setMedicationSourcePreviewTitle("Review Medication Source");
        setMedicationSourcePreviewEntries([]);
        setMedicationSourceType("");
        setMedicationSourceDiagnosisText("");
        setMedicationSourcePreviewWarnings([]);
        setDelegationDraftJobDialogVisible(false);
        setDelegationDraftJobId("");
        setDelegationDraftJobStatus("");
        setDelegationDraftJobSubStatus("");
        setDelegationDraftElapsedSeconds(0);
        setDelegationDraftGeneratedForms([]);
        setDelegationDraftResultMessage("");
        setDelegationDraftErrorMessage("");
    };

    const closeMedicationSourcePreviewDialog = () => {
        if (importingMedicationSource) {
            return;
        }
        resetMedicationSourceState();
    };

    const closeAssessmentVersionDialog = () => {
        if (loadingAssessmentVersions || importingMedicationSource) {
            return;
        }
        setShowAssessmentVersionDialog(false);
    };

    const closeNcpVersionDialog = () => {
        if (loadingNcpVersions || importingMedicationSource) {
            return;
        }
        setShowNcpVersionDialog(false);
    };

    const openCarePlanRequiredDialog = () => {
        setShowCarePlanRequiredDialog(true);
    };

    const closeCarePlanRequiredDialog = () => {
        setImportingMedicationSource(false);
        setShowCarePlanRequiredDialog(false);
    };

    const resetDelegationDraftReuseState = () => {
        setShowDelegationDraftReuseDialog(false);
        setDelegationDraftReuseForms([]);
        setDelegationDraftReuseSourceType("");
        setDelegationDraftReuseOptions(null);
    };

    const beginDelegationDraftProgress = (subStatus: string) => {
        setDelegationDraftJobId("");
        setDelegationDraftJobStatus("SUBMITTED");
        setDelegationDraftJobSubStatus(subStatus);
        setDelegationDraftGeneratedForms([]);
        setDelegationDraftResultMessage("");
        setDelegationDraftErrorMessage("");
        setDelegationDraftElapsedSeconds(0);
        setDelegationDraftJobDialogVisible(true);
    };

    const onChooseAssessmentDraftSource = () => {
        void openStaffSelection("assessment");
    };

    const onChooseNcpDraftSource = () => {
        void openStaffSelection("ncp");
    };

    const onChooseManualForm = (formId: string) => {
        const form = DELEGATION_FORMS.find((item) => item.id === formId);
        if (!form) return;
        setSelectedFormId(form.id);
        if (form.id === "13-678") {
            setPendingDelegationTaskForm(form);
            setDelegationTaskSelectionMode(true);
            setShowDelegationTaskDialog(true);
        } else if (isStaffSpecificDelegationForm(form.id)) {
            void openStaffSelection("manual");
        } else {
            void createNewFormThread(form);
        }
    };

    const delegationFormMenuItems = [
        {
            label: "Generate from Assessment",
            icon: "pi pi-file-edit",
            command: onChooseAssessmentDraftSource,
        },
        {
            label: "Generate from Care Plan",
            icon: "pi pi-heart",
            command: onChooseNcpDraftSource,
        },
        {
            label: "Create New Manually",
            icon: "pi pi-plus-circle",
            items: DELEGATION_FORMS.map((form) => ({
                label: `${form.id} - ${form.title}`,
                command: () => onChooseManualForm(form.id),
            })),
        },
    ];

    const continueDelegationTaskSelection = (tasks: any[]) => {
        const form = pendingDelegationTaskForm;
        setShowDelegationTaskDialog(false);
        setDelegationTaskSelectionMode(false);
        setPendingDelegationTaskForm(null);
        if (form && tasks.length) {
            void (async () => {
                for (const task of tasks) {
                    await createNewFormThread(form, task);
                }
            })();
        }
    };

    const openStaffSelection = async (action: "manual" | "assessment" | "ncp") => {
        if (!residentId) return;
        setStaffSelectionAction(action);
        setLoadingStaffOptions(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderDelegationApi
                ? await backendAPIStore.getProviderDelegationStaff(residentId)
                : await backendAPIStore.getNurseDelegationStaff(residentId);
            const staff = Array.isArray(response?.staff) ? response.staff : [];
            if (!staff.length) {
                setStaffOptions([]);
                setSelectedStaffIds([]);
                setShowStaffSelectionDialog(true);
                return;
            }
            setStaffOptions(staff);
            setSelectedStaffIds(staff
                .filter((item: any) => hasStaffDelegationRequirements(item))
                .map(getDelegationStaffId)
                .filter(Boolean));
            setShowStaffSelectionDialog(true);
        } catch (error: any) {
            toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load staff members.", life: 4000 });
        } finally {
            setLoadingStaffOptions(false);
        }
    };

    const continueStaffSelection = () => {
        if (!staffSelectionAction) return;
        const action = staffSelectionAction;
        setShowStaffSelectionDialog(false);
        if (action === "manual") {
            void createNewFormThread();
        } else {
            window.setTimeout(() => {
                if (action === "assessment") {
                    void loadAssessmentVersionsForCarePlanImport();
                } else {
                    void loadNcpVersionsForImport();
                }
            }, 0);
        }
    };

    const isMissingCarePlanMedicationError = (error: any) => {
        const normalizedMessage = String(error?.message || "").trim().toLowerCase();
        return normalizedMessage === "no medication entries were found in the resident care plan.".toLowerCase()
            || normalizedMessage === "no medication entries were found in the selected assessment version.".toLowerCase()
            || normalizedMessage === "no medication entries were found in the negotiated care plan.".toLowerCase()
            || normalizedMessage === "no medication entries were found in the selected negotiated care plan version.".toLowerCase();
    };

    const handleMedicationSourceImportError = (error: any, sourceLabel: string) => {
        if (isMissingCarePlanMedicationError(error)) {
            resetMedicationSourceState();
            setMissingMedicationSourceLabel(sourceLabel);
            openCarePlanRequiredDialog();
            return;
        }

        resetMedicationSourceState();
        toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || `Unable to read medications from ${sourceLabel.toLowerCase()}.`,
            life: 4000,
        });
    };

    const getResidentMedicationDiagnosisText = async (assessmentRecords?: any[]) => {
        if (!residentId) {
            return "";
        }
        if (usesProviderCarePlanSource) {
            const carePlanRows = await backendAPIStore.getResidentCarePlan(residentId);
            const summarySection = (Array.isArray(carePlanRows) ? carePlanRows : []).find(
                (item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY,
            );
            const medicationSection = (Array.isArray(carePlanRows) ? carePlanRows : []).find(
                (item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT,
            );
            return String(summarySection?.medicalDiagnosesDetails || medicationSection?.medicationAmountReasonableDetails || "").trim();
        }

        const step04Record = (Array.isArray(assessmentRecords) ? assessmentRecords : []).find((item: any) => item?.recordType === "STEP#04") || null;
        const diagnoses = Array.isArray(step04Record?.diagnoses)
            ? step04Record.diagnoses
                .map((item: any) => String(item?.description || "").trim())
                .filter(Boolean)
            : [];
        const recentHistory = Array.isArray(step04Record?.recentHistory)
            ? step04Record.recentHistory
                .map((item: any) => String(item?.text || "").trim())
                .filter(Boolean)
            : [];

        return [...diagnoses, ...recentHistory].join("\n").trim();
    };

    const getAssessmentMedicationEntriesFromRecords = (records: any[]) => {
        const step05Record = (Array.isArray(records) ? records : []).find((item: any) => item?.recordType === "STEP#05") || null;
        return normalizeMedicationSourceEntries(step05Record?.medications || []);
    };

    const getNcpMedicationSourceFromRecords = (records: any[]) => {
        const summarySection = (Array.isArray(records) ? records : []).find(
            (item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.SUMMARY,
        ) || null;
        const medicationSection = (Array.isArray(records) ? records : []).find(
            (item: any) => item?.carePlanType === RESIDENT_CARE_PLAN_TYPE.MEDICATION_MANAGEMENT,
        ) || null;
        return {
            entries: getMedicationEntriesFromCarePlanSection(medicationSection),
            diagnosisText: String(summarySection?.medicalDiagnosesDetails || medicationSection?.medicationAmountReasonableDetails || "").trim(),
        };
    };

    const getAssessmentVersionOptionLabel = (form: any) => {
        const isCurrentVersion = String(form?.formId || "").trim() === currentAssessmentVersionFormId;
        const savedAt = form?.modifiedAt || form?.updatedAt || form?.createdAt;
        return `${isCurrentVersion ? "Current Version - " : ""}Version ${form?.version || 1}${savedAt ? ` • ${new Date(savedAt).toLocaleString()}` : ""}`;
    };

    const getCurrentAssessmentVersionSummary = () => {
        const currentVersion = assessmentVersionOptions.find(
            (form: any) => String(form?.formId || "").trim() === currentAssessmentVersionFormId,
        );
        if (!currentVersion) {
            return "";
        }
        return getAssessmentVersionOptionLabel(currentVersion);
    };

    const getNcpVersionOptionLabel = (form: any) => {
        const isCurrentVersion = String(form?.formId || "").trim() === currentNcpVersionFormId;
        const savedAt = form?.modifiedAt || form?.updatedAt || form?.createdAt;
        return `${isCurrentVersion ? "Current Version - " : ""}Version ${form?.version || 1}${savedAt ? ` • ${new Date(savedAt).toLocaleString()}` : ""}`;
    };

    const getCurrentNcpVersionSummary = () => {
        const currentVersion = ncpVersionOptions.find(
            (form: any) => String(form?.formId || "").trim() === currentNcpVersionFormId,
        );
        if (!currentVersion) {
            return "";
        }
        return getNcpVersionOptionLabel(currentVersion);
    };

    const loadAssessmentVersionsForCarePlanImport = async () => {
        if (!residentId) {
            return;
        }

        setLoadingAssessmentVersions(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderDelegationApi
                ? await backendAPIStore.getProviderAssessmentForms(residentId)
                : await backendAPIStore.getNurseAssessmentForms(residentId);
            const forms = Array.isArray(response?.forms) ? response.forms : Array.isArray(response) ? response : [];
            const sortedForms = [...forms].sort((a: any, b: any) => {
                const versionDelta = Number(b?.version || 0) - Number(a?.version || 0);
                if (versionDelta !== 0) {
                    return versionDelta;
                }
                const leftTime = String(b?.modifiedAt || b?.updatedAt || b?.createdAt || "");
                const rightTime = String(a?.modifiedAt || a?.updatedAt || a?.createdAt || "");
                return leftTime.localeCompare(rightTime);
            });

            if (!sortedForms.length) {
                throw new Error("No assessment versions were found for this resident yet.");
            }

            setAssessmentVersionOptions(sortedForms);
            setCurrentAssessmentVersionFormId(String(response?.activeFormId || sortedForms[0]?.formId || "").trim());
            setSelectedAssessmentVersionFormId(String(response?.activeFormId || sortedForms[0]?.formId || "").trim());
            setShowAssessmentVersionDialog(true);
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to load assessment versions for this resident.",
                life: 4000,
            });
        } finally {
            setLoadingAssessmentVersions(false);
        }
    };

    const loadNcpVersionsForImport = async () => {
        if (!residentId) {
            return;
        }

        if (usesProviderCarePlanSource) {
            void openNegotiatedCarePlanMedicationDraftPreview();
            return;
        }

        setLoadingNcpVersions(true);
        try {
            await backendAPIStore.init();
            const response: any = await backendAPIStore.getNurseNcpForms(residentId);
            const forms = Array.isArray(response?.forms) ? response.forms : [];
            const sortedForms = [...forms].sort((a: any, b: any) => {
                const versionDelta = Number(b?.version || 0) - Number(a?.version || 0);
                if (versionDelta !== 0) {
                    return versionDelta;
                }
                const leftTime = String(b?.modifiedAt || b?.updatedAt || b?.createdAt || "");
                const rightTime = String(a?.modifiedAt || a?.updatedAt || a?.createdAt || "");
                return leftTime.localeCompare(rightTime);
            });

            if (!sortedForms.length) {
                throw new Error("No negotiated care plan versions were found for this resident yet.");
            }

            setNcpVersionOptions(sortedForms);
            setCurrentNcpVersionFormId(String(response?.activeFormId || sortedForms[0]?.formId || "").trim());
            setSelectedNcpVersionFormId(String(response?.activeFormId || sortedForms[0]?.formId || "").trim());
            setShowNcpVersionDialog(true);
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to load negotiated care plan versions for this resident.",
                life: 4000,
            });
        } finally {
            setLoadingNcpVersions(false);
        }
    };

    const createDelegationDraftsFromMedicationSource = async (
        sourceType: "carePlan" | "ncp",
        medicationEntries: any[],
        options?: {
            sourceLabel?: string;
            assessmentFormId?: string;
            ncpFormId?: string;
            forceRegenerate?: boolean;
        },
        useExistingCredit: boolean = false,
    ) => {
        if (!residentId || medicationEntries.length === 0) {
            return;
        }

        setImportingMedicationSource(true);
        try {
            await backendAPIStore.init();
            const response: any = usesProviderDelegationApi
                ? await backendAPIStore.createProviderDelegationDraftsFromMedicationSource({
                    residentId,
                    sourceType,
                    sourceLabel: options?.sourceLabel || (sourceType === "ncp" ? "Negotiated Care Plan" : "Manage Resident Assessment"),
                    medicationEntries,
                    sourceFormId: sourceType === "ncp"
                        ? (options?.ncpFormId || undefined)
                        : (options?.assessmentFormId || undefined),
                    formId: sourceType === "carePlan" ? options?.assessmentFormId || undefined : undefined,
                    forceRegenerate: options?.forceRegenerate === true,
                    useExistingCredit,
                    staffIds: selectedStaffIds,
                })
                : await backendAPIStore.createNurseDelegationDraftsFromMedicationSource({
                    residentId,
                    sourceType,
                    sourceLabel: options?.sourceLabel || (sourceType === "ncp" ? "Negotiated Care Plan" : "Manage Resident Assessment"),
                    medicationEntries,
                    sourceFormId: sourceType === "ncp"
                        ? (options?.ncpFormId || undefined)
                        : (options?.assessmentFormId || undefined),
                    formId: sourceType === "carePlan" ? options?.assessmentFormId || undefined : undefined,
                    forceRegenerate: options?.forceRegenerate === true,
                    useExistingCredit,
                    staffIds: selectedStaffIds,
                });
            if (response?.reusedExisting === true) {
                setDelegationDraftJobDialogVisible(false);
                setDelegationDraftJobId("");
                setDelegationDraftJobStatus("");
                setDelegationDraftJobSubStatus("");
                setDelegationDraftElapsedSeconds(0);
                setDelegationDraftGeneratedForms([]);
                setDelegationDraftResultMessage("");
                setDelegationDraftErrorMessage("");
                setDelegationDraftReuseForms(Array.isArray(response?.forms) ? response.forms : []);
                setDelegationDraftReuseSourceType(sourceType);
                setDelegationDraftReuseOptions({
                    sourceLabel: options?.sourceLabel,
                    assessmentFormId: options?.assessmentFormId,
                    ncpFormId: options?.ncpFormId,
                });
                setShowDelegationDraftReuseDialog(true);
                return;
            }
            if (response?.jobId) {
                setDelegationDraftJobId(String(response.jobId || "").trim());
                setDelegationDraftJobStatus(String(response?.jobStatus || "SUBMITTED").trim());
                setDelegationDraftJobSubStatus(String(response?.jobSubStatus || "Queued").trim());
                setDelegationDraftGeneratedForms([]);
                setDelegationDraftResultMessage("");
                setDelegationDraftErrorMessage("");
                setDelegationDraftJobDialogVisible(true);
                return;
            }

            const createdForms = Array.isArray(response?.forms) ? response.forms : [];
            if (!createdForms.length) {
                throw new Error(response?.message || "No delegation draft forms were created from this source.");
            }

            toast.current?.show({
                severity: "success",
                summary: "Delegation drafts created",
                detail: response?.message || `${createdForms.length} delegation draft form${createdForms.length === 1 ? "" : "s"} ready from this source.`,
                life: 3500,
            });
            resetMedicationSourceState();
            openDelegationForm(String(createdForms[0]?.formCode || ""), createdForms[0]?.formId);
        } catch (error: any) {
            const code = String(error?.code || error?.cause?.message || "").trim().toUpperCase();
            if (code === "DELEGATION_JOB_ALREADY_RUNNING") {
                const currentJobId = String(error?.currentJobId || error?.details?.currentJobId || "").trim();
                const currentJobStatus = String(error?.currentJobStatus || error?.details?.currentJobStatus || "SUBMITTED").trim();
                if (currentJobId) {
                    setDelegationDraftJobId(currentJobId);
                    setDelegationDraftJobStatus(currentJobStatus || "SUBMITTED");
                    setDelegationDraftJobSubStatus("Resuming current delegation draft job");
                    setDelegationDraftGeneratedForms([]);
                    setDelegationDraftResultMessage("");
                    setDelegationDraftErrorMessage("");
                    setDelegationDraftJobDialogVisible(true);
                    return;
                }
                resetMedicationSourceState();
                toast.current?.show({
                    severity: "warn",
                    summary: "Delegation Draft Already Running",
                    detail: error?.message || "A delegation draft job is already running for this resident.",
                    life: 4000,
                });
                return;
            }
            if (code === "SUBSCRIPTION_LIMIT_REACHED") {
                setDelegationDraftJobDialogVisible(false);
                setDelegationDraftJobId("");
                setDelegationDraftJobStatus("");
                setDelegationDraftJobSubStatus("");
                setDelegationDraftGeneratedForms([]);
                setDelegationDraftResultMessage("");
                setDelegationDraftErrorMessage("");
                setDelegationDraftElapsedSeconds(0);
                const availableCredits = Number(
                    error?.automationCreditsAvailable
                    || error?.details?.automationCreditsAvailable
                    || error?.details?.featureCreditBalances?.ncp_generation
                    || 0,
                );
                if (requiresProviderSubscriptionMessageOnly) {
                    setAutomationCreditsAvailable(0);
                    setFeatureAccessDialogAllowsCreditUse(false);
                    setFeatureAccessDialogMessage("The provider must have an active subscription to use the shared resident delegation workspace.");
                    delegationFeatureAccessRetryActionRef.current = null;
                    setShowFeatureAccessDialog(true);
                    return;
                }
                setAutomationCreditsAvailable(availableCredits);
                const canUseCreditNow = availableCredits > 0 && !useExistingCredit;
                setFeatureAccessDialogAllowsCreditUse(canUseCreditNow);
                setFeatureAccessDialogMessage(
                    availableCredits > 0
                        ? "Your monthly draft uses are used up. Use 1 automation credit to continue."
                        : "No automation credits are available for delegation drafting. Buy Automation Credits to continue.",
                );
                delegationFeatureAccessRetryActionRef.current = canUseCreditNow
                    ? (() => {
                        void createDelegationDraftsFromMedicationSource(sourceType, medicationEntries, options, true);
                    })
                    : null;
                setShowFeatureAccessDialog(true);
                return;
            }
            resetMedicationSourceState();
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to start delegation draft generation from this source.",
                life: 4500,
            });
        } finally {
            setImportingMedicationSource(false);
        }
    };

    const openAssessmentMedicationDraftPreview = async (forceRegenerate = false) => {
        if (!residentId) {
            return;
        }
        beginDelegationDraftProgress("Preparing assessment details");
        setImportingMedicationSource(true);
        try {
            await backendAPIStore.init();
            const records: any[] = usesProviderDelegationApi
                ? await backendAPIStore.getProviderAssessmentFormSteps(selectedAssessmentVersionFormId)
                : await backendAPIStore.getNurseAssessmentFormSteps(selectedAssessmentVersionFormId);
            const detectedEntries = getAssessmentMedicationEntriesFromRecords(records);
            if (!detectedEntries.length) {
                throw new Error("No medication entries were found in the selected assessment version.");
            }
            const diagnosisText = await getResidentMedicationDiagnosisText(records);
            setMedicationSourcePreviewEntries(detectedEntries);
            setMedicationSourceDiagnosisText(diagnosisText);
            setMedicationSourceType("carePlan");
            setMedicationSourcePreviewWarnings([]);
            await createDelegationDraftsFromMedicationSource("carePlan", detectedEntries, {
                sourceLabel: "Manage Resident Assessment",
                assessmentFormId: selectedAssessmentVersionFormId,
                forceRegenerate,
            });
        } catch (error: any) {
            handleMedicationSourceImportError(error, "Manage Resident Assessment");
        }
    };

    const openNegotiatedCarePlanMedicationDraftPreview = async (forceRegenerate = false) => {
        if (!residentId) {
            return;
        }

        beginDelegationDraftProgress("Preparing negotiated care plan details");
        setImportingMedicationSource(true);
        try {
            await backendAPIStore.init();

            if (usesProviderCarePlanSource) {
                const carePlanRows = await backendAPIStore.getResidentCarePlan(residentId);
                const { entries, diagnosisText } = getNcpMedicationSourceFromRecords(Array.isArray(carePlanRows) ? carePlanRows : []);
                if (!entries.length) {
                    throw new Error("No medication entries were found in the negotiated care plan.");
                }
                setMedicationSourcePreviewEntries(entries);
                setMedicationSourceDiagnosisText(diagnosisText);
                setMedicationSourceType("ncp");
                setMedicationSourcePreviewWarnings([]);
                await createDelegationDraftsFromMedicationSource("ncp", entries, {
                    sourceLabel: "Negotiated Care Plan",
                    forceRegenerate,
                });
                return;
            }

            const records: any[] = await backendAPIStore.getNurseNcpForms(undefined, selectedNcpVersionFormId);
            const { entries, diagnosisText } = getNcpMedicationSourceFromRecords(records);
            if (!entries.length) {
                throw new Error("No medication entries were found in the selected negotiated care plan version.");
            }
            setMedicationSourcePreviewEntries(entries);
            setMedicationSourceDiagnosisText(diagnosisText);
            setMedicationSourceType("ncp");
            setMedicationSourcePreviewWarnings([]);
            await createDelegationDraftsFromMedicationSource("ncp", entries, {
                sourceLabel: "Negotiated Care Plan",
                ncpFormId: selectedNcpVersionFormId,
                forceRegenerate,
            });
        } catch (error: any) {
            handleMedicationSourceImportError(error, "Negotiated Care Plan");
        }
    };

    const handleRegenerateDelegationDrafts = async () => {
        if (!delegationDraftReuseSourceType || !delegationDraftReuseOptions) {
            return;
        }
        resetDelegationDraftReuseState();
        if (delegationDraftReuseSourceType === "carePlan") {
            await openAssessmentMedicationDraftPreview(true);
            return;
        }
        await openNegotiatedCarePlanMedicationDraftPreview(true);
    };

    const startNewVersion = async (
        formConfig?: typeof DELEGATION_FORMS[number],
        threadSummary?: { threadId: string; threadLabel?: string; latestForm?: any } | null,
    ) => {
        const resolvedFormConfig = formConfig || selectedForm;
        if (!residentId || !resolvedFormConfig) return;
        try {
            await backendAPIStore.init();
            const nextTemplateDefinition = getDelegationTemplateDefinition(resolvedFormConfig.id);
            const created: any = usesProviderDelegationApi
                ? await backendAPIStore.createProviderDelegationForm(residentId, resolvedFormConfig.id, resolvedFormConfig.title, {
                    formThreadId: threadSummary?.threadId || "",
                    formThreadLabel: threadSummary?.threadLabel || "",
                    templateVersion: nextTemplateDefinition?.templateVersion,
                    templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                    templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                    templateDefinition: nextTemplateDefinition,
                    staffIds: threadSummary?.latestForm?.selectedStaffIds || selectedStaffIds,
                })
                : await backendAPIStore.createNurseDelegationForm(residentId, resolvedFormConfig.id, resolvedFormConfig.title, {
                    formThreadId: threadSummary?.threadId || "",
                    formThreadLabel: threadSummary?.threadLabel || "",
                    templateVersion: nextTemplateDefinition?.templateVersion,
                    templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                    templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                    templateDefinition: nextTemplateDefinition,
                    staffIds: isStaffSpecificDelegationForm(resolvedFormConfig.id)
                        ? threadSummary?.latestForm?.selectedStaffIds || selectedStaffIds
                        : undefined,
                });
            toast.current?.show({
                severity: "success",
                summary: "New version created",
                detail: `${threadSummary?.threadLabel ? `${threadSummary.threadLabel} ` : ""}version ${created?.version || ""} is ready for editing.`,
                life: 3000,
            });
            setSelectedFormId(resolvedFormConfig.id);
            setSelectedVersionThreadKey(getDelegationThreadKeyForForm(resolvedFormConfig.id, {
                ...(created || {}),
                formThreadId: created?.formThreadId || threadSummary?.threadId,
                selectedStaffIds: created?.selectedStaffIds || threadSummary?.latestForm?.selectedStaffIds,
                selectedStaff: created?.selectedStaff || threadSummary?.latestForm?.selectedStaff,
            }));
            navigate(
                isSharedResidentMode
                    ? RouterAddress.getSharedDelegationFormPagePath(homeId || "", residentId, resolvedFormConfig.id, created?.formId)
                    : isProviderResidentMode && homeId
                        ? RouterAddress.getProviderDelegationFormPagePath(homeId, residentId, resolvedFormConfig.id, created?.formId)
                        : isProviderNurseResidentMode
                            ? RouterAddress.getProviderNurseDelegationFormPagePath(residentId, resolvedFormConfig.id, created?.formId)
                            : RouterAddress.getNurseDelegationFormPagePath(residentId, resolvedFormConfig.id, created?.formId)
            );
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to start a new delegation form version.",
                life: 4000,
            });
        }
    };

    const createNewFormThread = async (formConfig?: typeof DELEGATION_FORMS[number], delegationTask?: any) => {
        const resolvedForm = formConfig || selectedForm;
        if (!residentId || !resolvedForm) return;
        setCreatingThreadForm(true);
        try {
            await backendAPIStore.init();
            const nextTemplateDefinition = getDelegationTemplateDefinition(resolvedForm.id);
            const payload = {
                templateVersion: nextTemplateDefinition?.templateVersion,
                templateSchemaVersion: nextTemplateDefinition?.schemaVersion,
                templateRevisionLabel: nextTemplateDefinition?.printHeader?.revision,
                templateDefinition: nextTemplateDefinition,
                staffIds: isStaffSpecificDelegationForm(resolvedForm.id) ? selectedStaffIds : undefined,
                delegationTask: resolvedForm.id === "13-678" ? delegationTask : undefined,
            };
            const created: any = usesProviderDelegationApi
                ? await backendAPIStore.createProviderDelegationForm(residentId, resolvedForm.id, resolvedForm.title, payload)
                : await backendAPIStore.createNurseDelegationForm(residentId, resolvedForm.id, resolvedForm.title, payload);
            if (isStepActive("nurse-delegation-create-form")) {
                completeStep("nurse-delegation-create-form");
            }
            const createdFormIds = (Array.isArray(created?.forms) ? created.forms : [created])
                .map((form: any) => String(form?.formId || "").trim())
                .filter(Boolean);
            setHighlightedDelegationFormIds(createdFormIds);
            setSelectedFormId(resolvedForm.id);
            await refreshDelegationForms();
            if (createdFormIds.length) {
                window.setTimeout(() => {
                    delegationFormRowRefs.current[createdFormIds[0]]?.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 100);
            }
            toast.current?.show({
                severity: "success",
                summary: Number(created?.createdCount || created?.forms?.length || 1) > 1 ? "Delegation forms created" : "New form created",
                detail: Number(created?.createdCount || created?.forms?.length || 1) > 1
                    ? `${created.createdCount || created.forms.length} staff-specific forms were created.`
                    : `${created?.formDisplayTitle || resolvedForm.title} is ready for editing.`,
                life: 3000,
            });
        } catch (error: any) {
            toast.current?.show({
                severity: "error",
                summary: getErrorTitle(error),
                detail: error?.message || "Unable to create a new delegation form.",
                life: 4000,
            });
        } finally {
            setCreatingThreadForm(false);
        }
    };

    const getNextStepLabel = (formCode: string, formThreadId?: string, staffKey?: string) => {
        const residentDraft = getResidentDraftForForm(formCode, formThreadId, staffKey);
        return residentDraft
            ? `Continue Version ${residentDraft.version || 1} to keep the resident's current draft moving.`
            : "Start a new version for the resident's next delegation cycle.";
    };
    const nextStepLabel = selectedForm && selectedThreadSummary
        ? getNextStepLabel(selectedForm.id, selectedThreadSummary.threadId, getDelegationStaffKey(selectedThreadSummary.latestForm))
        : "";
    const openSelectedForm = (formId?: string) => {
        if (!selectedForm) return;
        openDelegationForm(selectedForm.id, formId);
    };

    const downloadDelegationForm = async (formId?: string) => {
        const resolvedFormId = String(formId || "").trim();
        if (!resolvedFormId) {
            throw new Error("No delegation form version is available to download yet.");
        }

        await backendAPIStore.init();
        const response: any = usesProviderDelegationApi
            ? await backendAPIStore.generateProviderDelegationDocument(resolvedFormId)
            : await backendAPIStore.generateNurseDelegationDocument(resolvedFormId);
        const documentUrl = String(response?.documentUrl || "").trim();
        if (!documentUrl) {
            throw new Error(response?.message || "Delegation document could not be generated.");
        }

        openUrlInNewTab(documentUrl);
        toast.current?.show({
            severity: "success",
            summary: "Download started",
            detail: "The delegation document is being downloaded.",
            life: 3000,
        });
    };

    const deleteDelegationForm = (form: any) => {
        const formId = String(form?.formId || "").trim();
        if (!formId) return;
        confirmDialog({
            header: "Delete Delegation Form",
            message: `Delete ${getDelegationDisplayTitle(selectedForm || {}, form, residentDisplayName)}?`,
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            accept: () => {
                void (async () => {
                    try {
                        await backendAPIStore.init();
                        if (usesProviderDelegationApi) {
                            await backendAPIStore.deleteProviderDelegationForm(formId);
                        } else {
                            await backendAPIStore.deleteNurseDelegationForm(formId);
                        }
                        await refreshDelegationForms();
                        toast.current?.show({ severity: "success", summary: "Form deleted", detail: "The delegation form was deleted.", life: 3000 });
                    } catch (error: any) {
                        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to delete the delegation form.", life: 4000 });
                    }
                })();
            },
        });
    };

    const deleteAllDelegationForms = () => {
        if (!residentId || !savedFormThreads.length) return;
        confirmDialog({
            header: "Delete All Delegation Forms",
            message: `Delete all ${savedFormThreads.length} saved delegation form${savedFormThreads.length === 1 ? "" : "s"} for this resident? This action cannot be undone.`,
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Delete All",
            rejectLabel: "Cancel",
            accept: () => {
                void (async () => {
                    try {
                        await backendAPIStore.init();
                        if (usesProviderDelegationApi) {
                            await backendAPIStore.deleteAllProviderDelegationForms(residentId);
                        } else {
                            await backendAPIStore.deleteAllNurseDelegationForms(residentId);
                        }
                        setSelectedVersionThreadKey("");
                        await refreshDelegationForms();
                        toast.current?.show({ severity: "success", summary: "Forms deleted", detail: "All saved delegation forms were deleted.", life: 3000 });
                    } catch (error: any) {
                        toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to delete the delegation forms.", life: 4000 });
                    }
                })();
            },
        });
    };

    const openStaffManagement = () => {
        const providerId = isSharedResidentMode
            ? `shared:${String(homeId || "").trim()}`
            : String(residentProfile?.providerId || residentProfile?.nurseProviderId || "").trim();
        if (!providerId && homeId) {
            navigate(RouterAddress.getHomeStaffManagementPagePath(homeId));
            return;
        }
        setShowManageStaffDialog(true);
    };

    return (
        <div style={{ padding: "1.25rem" }}>
            <Toast ref={toast} />
            <div style={dashboardPageContainerStyle}>
                {renderDashboardHeader(
                    `Delegation Forms${residentProfile?.firstName || residentProfile?.lastName ? ` - ${`${residentProfile?.firstName || ""} ${residentProfile?.lastName || ""}`.trim()}` : ""}`,
                    "Manage nurse delegation forms and open official references from one place.",
                    <div className="flex gap-2 flex-wrap align-items-center">
                        <div>
                            <Button
                                type="button"
                                label="Create Delegation Form"
                                severity="secondary"
                                rounded
                                outlined
                                size="small"
                                icon="pi pi-chevron-down"
                                iconPos="right"
                                onClick={(event) => delegationFormMenuRef.current?.toggle(event)}
                                disabled={!residentId || importingMedicationSource}
                            />
                            <TieredMenu
                                model={delegationFormMenuItems}
                                popup
                                ref={delegationFormMenuRef}
                                breakpoint="960px"
                                style={{ minWidth: "22rem" }}
                            />
                        </div>
                        <Button
                            type="button"
                            label="Manage Nursing Tasks"
                            icon="pi pi-list"
                            severity="warning"
                            rounded
                            outlined
                            onClick={() => {
                                setDelegationTaskSelectionMode(false);
                                setShowDelegationTaskDialog(true);
                            }}
                            disabled={!residentId}
                        />
                        <Button
                            type="button"
                            label="Manage Staff"
                            icon="pi pi-users"
                            severity="secondary"
                            rounded
                            outlined
                            onClick={openStaffManagement}
                            disabled={!residentId}
                        />
                    </div>,
                )}

                {loadingProfile ? (
                    <div className="flex justify-content-center" style={{ padding: "2rem" }}>
                        <ProgressSpinner />
                    </div>
                ) : (
                    <>
                        {savedFormThreads.length ? (
                            <div style={{ marginBottom: "1rem" }}>
                                <div className="flex align-items-center justify-content-between gap-2 flex-wrap" style={{ marginBottom: "0.7rem" }}>
                                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Saved Delegation Forms for this resident ({savedFormThreads.length})</div>
                                </div>
                                <div
                                    style={{
                                        border: "1px solid #dbe7f3",
                                        borderRadius: "14px",
                                        overflow: "hidden",
                                        background: "#ffffff",
                                    }}
                                >
                                    <Tooltip target=".delegation-form-description-help" position="top" />
                                    {!isMobile ? (
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "minmax(280px, 1fr) minmax(120px, 140px) minmax(160px, 190px) minmax(250px, 300px)",
                                                gap: "0.75rem",
                                                padding: "0.8rem 1rem",
                                                background: "#f8fbff",
                                                borderBottom: "1px solid #dbe7f3",
                                                fontSize: "0.76rem",
                                                fontWeight: 800,
                                                letterSpacing: "0.05em",
                                                textTransform: "uppercase",
                                                color: "#64748b",
                                            }}
                                        >
                                            <div>Form</div>
                                            <div>Status</div>
                                            <div>Last Edited</div>
                                            <div>Actions</div>
                                        </div>
                                    ) : null}
                                    {savedFormThreads.map((thread) => {
                                        const summary = savedDraftSummary[thread.threadKey];
                                        const isSelected = selectedFormId === thread.formCode && activeVersionThreadKey === thread.threadKey;
                                        const latestForm = thread.latestForm;
                                        const isRecentlyGenerated = highlightedDelegationFormIds.includes(String(latestForm?.formId || "").trim());
                                        const latestFormStatus = latestForm?.status;
                                        const rowHasVersionHistory = thread.forms.length > 1;
                                        const actionItems = [
                                            {
                                                label: "Start New Version",
                                                icon: "pi pi-plus-circle",
                                                command: () => { void startNewVersion(thread.formConfig, thread); },
                                            },
                                            ...(rowHasVersionHistory ? [{
                                                label: "Show Previous Versions",
                                                icon: "pi pi-history",
                                                command: () => {
                                                    setSelectedFormId(thread.formCode);
                                                    setSelectedVersionThreadKey(thread.threadKey);
                                                    setShowVersionsDialog(true);
                                                },
                                            }] : []),
                                            {
                                                label: "Delete",
                                                icon: "pi pi-trash",
                                                command: () => deleteDelegationForm(latestForm),
                                            },
                                        ];
                                        return (
                                            <div
                                                key={thread.threadKey}
                                                ref={(element) => {
                                                    const formId = String(latestForm?.formId || "").trim();
                                                    if (formId) delegationFormRowRefs.current[formId] = element;
                                                }}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: isMobile
                                                        ? "minmax(0, 1fr)"
                                                        : "minmax(280px, 1fr) minmax(120px, 140px) minmax(160px, 190px) minmax(250px, 300px)",
                                                    gap: isMobile ? "0.85rem" : "0.75rem",
                                                    alignItems: isMobile ? "stretch" : "center",
                                                    width: "100%",
                                                    borderBottom: "1px solid #eef2f7",
                                                    background: isSelected || isRecentlyGenerated ? "#fff7ed" : "#ffffff",
                                                    boxShadow: isRecentlyGenerated ? "inset 4px 0 0 #f97316" : undefined,
                                                    padding: "0.9rem 1rem",
                                                    transition: "background 0.15s ease",
                                                }}
                                            >
                                                <div style={{ minWidth: 0 }}>
                                                    {isMobile ? (
                                                        <div style={{ fontSize: "0.73rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.35rem" }}>
                                                            Form
                                                        </div>
                                                    ) : null}
                                                    <div style={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.35, marginBottom: "0.2rem" }}>
                                                        <span style={{ color: isSelected ? "#c2410c" : "#64748b" }}>{thread.formCode}</span>
                                                        <span style={{ color: "#94a3b8", margin: "0 0.35rem" }}>-</span>
                                                        {getDelegationDisplayTitle(thread.formConfig, thread.latestForm, residentDisplayName)}
                                                        <i
                                                            className="pi pi-question-circle delegation-form-description-help"
                                                            data-pr-tooltip={thread.formConfig.description}
                                                            aria-label="Form help"
                                                            style={{ color: "#64748b", fontSize: "0.82rem", marginLeft: "0.45rem", cursor: "help" }}
                                                        />
                                                    </div>
                                                    {isRecentlyGenerated ? (
                                                        <Tag value="Just created" severity="warning" rounded style={{ marginBottom: "0.35rem" }} />
                                                    ) : null}
                                                </div>
                                                <div>
                                                    {isMobile ? (
                                                        <div style={{ fontSize: "0.73rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.35rem" }}>
                                                            Status
                                                        </div>
                                                    ) : null}
                                                    <Tag
                                                        value={getDelegationStatusTagValue(latestFormStatus)}
                                                        severity={getDelegationStatusSeverity(latestFormStatus)}
                                                        rounded
                                                    />
                                                </div>
                                                <div style={{ color: "#64748b", lineHeight: 1.45, fontSize: "0.86rem" }}>
                                                    {isMobile ? (
                                                        <div style={{ fontSize: "0.73rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.35rem" }}>
                                                            Last Edited
                                                        </div>
                                                    ) : null}
                                                    {summary?.updatedAt ? new Date(summary.updatedAt).toLocaleString() : "—"}
                                                </div>
                                                <div
                                                    className={`flex gap-2 ${isMobile ? "flex-wrap justify-content-start" : "flex-nowrap justify-content-end align-items-center"}`}
                                                    style={{ whiteSpace: isMobile ? "normal" : "nowrap" }}
                                                >
                                                    {isMobile ? (
                                                        <div style={{ width: "100%", fontSize: "0.73rem", fontWeight: 800, letterSpacing: "0.05em", textTransform: "uppercase", color: "#94a3b8", marginBottom: "0.1rem" }}>
                                                            Actions
                                                        </div>
                                                    ) : null}
                                                    <Button
                                                        type="button"
                                                        label="Download"
                                                        icon="pi pi-download"
                                                        severity="warning"
                                                        outlined
                                                        rounded
                                                        onClick={() => {
                                                            void downloadDelegationForm(latestForm?.formId).catch((error: any) => {
                                                                toast.current?.show({
                                                                    severity: "error",
                                                                    summary: getErrorTitle(error),
                                                                    detail: error?.message || "Unable to download the delegation document.",
                                                                    life: 4000,
                                                                });
                                                            });
                                                        }}
                                                    />
                                                    <SplitButton
                                                        label="Open Form"
                                                        icon="pi pi-file-edit"
                                                        severity="warning"
                                                        rounded
                                                        outlined
                                                        onClick={() => {
                                                            setSelectedFormId(thread.formCode);
                                                            setSelectedVersionThreadKey(thread.threadKey);
                                                            openDelegationForm(thread.formCode, latestForm?.formId);
                                                        }}
                                                        model={actionItems}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="flex justify-content-center" style={{ marginTop: "0.85rem" }}>
                                    <Button
                                        type="button"
                                        label="Delete All Forms"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        outlined
                                        rounded
                                        size="small"
                                        onClick={deleteAllDelegationForms}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {/*
                            <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.7rem" }}>Selected Delegation Form</div>
                            {selectedForm ? (
                                <Card style={{ ...appSectionCardStyle, padding: "0.3rem 0.45rem" }}>
                                    <div className="flex justify-content-between align-items-start gap-3 flex-wrap">
                                        <div style={{ minWidth: 0, flex: "1 1 30rem" }}>
                                            <div className="flex align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.5rem" }}>
                                                <Tag value={selectedForm.id} severity="warning" rounded />
                                                <Tag value={selectedForm.editorMode === "structured" ? "Structured Form" : "Standard Form"} severity={selectedForm.editorMode === "structured" ? "success" : "info"} rounded />
                                                {selectedResidentDraft ? <Tag value={`Saved v${selectedResidentDraft.version || 1}`} severity="secondary" rounded /> : null}
                                            </div>
                                            <div style={{ fontSize: "1.06rem", fontWeight: 800, color: "#0f172a", marginBottom: "0.35rem" }}>
                                                {selectedForm.title}
                                            </div>
                                            <div style={{ color: "#64748b", lineHeight: 1.65 }}>
                                                {selectedForm.description}
                                            </div>
                                            <div style={{ marginTop: "0.8rem", padding: "0.8rem 0.95rem", borderRadius: "14px", border: "1px solid #dbe7f3", background: "#f8fbff" }}>
                                                <div style={{ fontSize: "0.76rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748b", marginBottom: "0.3rem" }}>
                                                    Next Step
                                                </div>
                                                <div style={{ color: "#0f172a", fontWeight: 700, lineHeight: 1.55 }}>
                                                    {nextStepLabel}
                                                </div>
                                            </div>
                                            {selectedResidentDraft ? (
                                                <div style={{ color: "#0f766e", marginTop: "0.55rem", fontWeight: 600 }}>
                                                    Current draft: Version {selectedResidentDraft.version || 1}
                                                    {" • "}
                                                    {selectedResidentDraft.updatedAt ? new Date(selectedResidentDraft.updatedAt).toLocaleString() : "Saved"}
                                                </div>
                                            ) : savedDraftSummary[selectedForm.id]?.count ? (
                                                <div style={{ color: "#0f766e", marginTop: "0.55rem", fontWeight: 600 }}>
                                                    {savedDraftSummary[selectedForm.id].count} saved draft(s)
                                                </div>
                                            ) : null}
                                            {selectedCompletedVersion ? (
                                                <div style={{ color: "#475569", marginTop: "0.45rem", fontWeight: 600 }}>
                                                    Last completed version: {selectedCompletedVersion.version || 1}
                                                </div>
                                            ) : null}
                                        <div className="flex gap-2 flex-wrap">
                                            <Button
                                                type="button"
                                                label={selectedResidentDraft ? "Continue Draft" : "Open Form"}
                                                severity="warning"
                                                rounded
                                                onClick={() => openSelectedForm()}
                                            />
                                            <Button
                                                type="button"
                                                label="Start New Version"
                                                severity="secondary"
                                                rounded
                                                outlined
                                                onClick={() => { void startNewVersion(); }}
                                            />
                                            {hasVersionHistory ? (
                                                <Button
                                                    type="button"
                                                    label="View Previous Revisions"
                                                    severity="secondary"
                                                    rounded
                                                    outlined
                                                    onClick={() => setShowVersionsDialog(true)}
                                                />
                                            ) : null}
                                            <Button
                                                type="button"
                                                label="Open Official Form"
                                                severity="secondary"
                                                rounded
                                                outlined
                                                onClick={() => openUrlInNewTab(selectedForm.officialUrl)}
                                            />
                                    </div>
                                </Card>
                            ) : (
                                <div style={{ color: "#64748b", fontWeight: 500 }}>No delegation forms match your search.</div>
                            )}
                        */}
                    </>
                )}
            </div>
            <Dialog
                header={selectedThreadSummary
                    ? `${selectedThreadSummary.forms?.[0]?.formCode || selectedForm?.id || "Delegation Form"} Version History${selectedThreadSummary.threadLabel ? ` - ${selectedThreadSummary.threadLabel}` : ""}`
                    : selectedForm ? `${selectedForm.id} Version History` : "Version History"}
                visible={showVersionsDialog}
                style={{ width: "min(56rem, 96vw)" }}
                onHide={() => setShowVersionsDialog(false)}
            >
                {selectedFormVersions.length ? (
                    <div>
                        {selectedFormVersions.map((form: any, index: number) => (
                            <div
                                key={form.formId || `${form.formCode}-${form.version}`}
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: "1rem",
                                    alignItems: "center",
                                    padding: "0.95rem 0",
                                    borderBottom: index === selectedFormVersions.length - 1 ? "none" : "1px solid #eef2f7",
                                    flexWrap: "wrap",
                                }}
                            >
                                <div style={{ minWidth: 0, flex: "1 1 24rem" }}>
                                    <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                        Version {form.version || 1}
                                    </div>
                                    <div style={{ color: "#64748b", marginTop: "0.25rem", lineHeight: 1.55 }}>
                                        Status: {getDelegationStatusLabel(form.status)}
                                        {" • "}
                                        {form.updatedAt ? new Date(form.updatedAt).toLocaleString() : "Saved"}
                                    </div>
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <Tag value={getDelegationStatusTagValue(form.status)} severity={getDelegationStatusSeverity(form.status)} rounded />
                                    <Button
                                        type="button"
                                        label="Open"
                                        size="small"
                                        severity="secondary"
                                        rounded
                                        outlined
                                        onClick={() => {
                                            setShowVersionsDialog(false);
                                            openDelegationForm(selectedForm?.id || form.formCode, form.formId);
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ color: "#64748b", fontWeight: 500 }}>
                        No previous revisions are available for this delegation form.
                    </div>
                )}
            </Dialog>
            <DelegationStaffManagementDialog
                providerId={isSharedResidentMode
                    ? `shared:${String(homeId || "").trim()}`
                    : String(residentProfile?.providerId || residentProfile?.nurseProviderId || "").trim()}
                residentId={residentId || ""}
                providerMode={isProviderResidentMode}
                editStaffId={staffEditId}
                visible={showManageStaffDialog}
                onHide={() => {
                    setStaffEditId("");
                    setShowManageStaffDialog(false);
                    if (staffSelectionAction) {
                        void openStaffSelection(staffSelectionAction);
                    }
                }}
                onBack={() => {
                    setStaffEditId("");
                    setShowManageStaffDialog(false);
                    if (staffSelectionAction) {
                        void openStaffSelection(staffSelectionAction);
                    }
                }}
                onSaved={async () => {
                    setStaffEditId("");
                    setShowManageStaffDialog(false);
                    if (staffSelectionAction) {
                        await openStaffSelection(staffSelectionAction || "manual");
                    }
                }}
            />
            <DelegationTaskManagementDialog
                visible={showDelegationTaskDialog}
                residentId={residentId || ""}
                providerMode={usesProviderDelegationApi}
                selectionMode={delegationTaskSelectionMode}
                backendAPIStore={backendAPIStore}
                onHide={() => {
                    setShowDelegationTaskDialog(false);
                    setDelegationTaskSelectionMode(false);
                    setPendingDelegationTaskForm(null);
                }}
                onContinue={continueDelegationTaskSelection}
            />
            <Dialog
                header="Select Staff for Delegation Form"
                visible={showStaffSelectionDialog}
                style={{ width: "min(42rem, 94vw)" }}
                onHide={() => {
                    if (!loadingStaffOptions) setShowStaffSelectionDialog(false);
                }}
                closable={!loadingStaffOptions}
            >
                <div style={{ color: "#475569", lineHeight: 1.65, marginBottom: "1rem" }}>
                    Select staff members when they are available. You can also continue without staff and assign them later.
                </div>
                {loadingStaffOptions ? (
                    <div className="flex justify-content-center" style={{ padding: "1.5rem" }}><ProgressSpinner /></div>
                ) : !staffOptions.length ? (
                    <div className="flex flex-column align-items-center text-center" style={{ padding: "1.5rem 1rem" }}>
                        <i className="pi pi-users" style={{ fontSize: "2rem", color: "#94a3b8", marginBottom: "0.75rem" }} />
                        <div style={{ fontWeight: 700, color: "#334155", marginBottom: "0.35rem" }}>No staff found</div>
                        <div style={{ color: "#64748b", marginBottom: "1rem" }}>Continue to create the form now, or add staff first if you want their details included.</div>
                        {!isSharedResidentMode ? (
                            <Button
                                type="button"
                                label="Add New Staff"
                                icon="pi pi-plus"
                                severity="warning"
                                rounded
                                onClick={() => {
                                    setShowStaffSelectionDialog(false);
                                    setShowManageStaffDialog(true);
                                }}
                            />
                        ) : null}
                    </div>
                ) : (
                    <>
                        <div className="flex justify-content-between align-items-center" style={{ marginBottom: "0.7rem" }}>
                            <span style={{ fontWeight: 700, color: "#334155" }}>Staff members</span>
                            <Button
                                type="button"
                                label={selectedStaffIds.length === staffOptions.filter((item: any) => hasStaffDelegationRequirements(item)).length ? "Clear all" : "Select all"}
                                size="small"
                                severity="secondary"
                                text
                                onClick={() => setSelectedStaffIds(
                                    selectedStaffIds.length === staffOptions.filter((item: any) => hasStaffDelegationRequirements(item)).length
                                        ? []
                                        : staffOptions
                                            .filter((item: any) => hasStaffDelegationRequirements(item))
                                            .map(getDelegationStaffId)
                                            .filter(Boolean),
                                )}
                            />
                        </div>
                        <div style={{ border: "1px solid #dbe7f3", borderRadius: "12px", overflow: "hidden" }}>
                            {staffOptions.map((staff: any, index: number) => {
                                const staffId = getDelegationStaffId(staff);
                                const hasRequirements = hasStaffDelegationRequirements(staff);
                                const checked = selectedStaffIds.includes(staffId);
                                return (
                                    <div key={staffId} className="flex align-items-center gap-3" style={{ padding: "0.8rem 0.9rem", borderBottom: index === staffOptions.length - 1 ? "none" : "1px solid #eef2f7" }}>
                                        <Checkbox
                                            inputId={`delegation-staff-${staffId}`}
                                            checked={checked}
                                            disabled={!hasRequirements}
                                            onChange={() => setSelectedStaffIds((current) => checked ? current.filter((id) => id !== staffId) : [...current, staffId])}
                                        />
                                        <span style={{ minWidth: 0 }}>
                                            <span style={{ display: "block", fontWeight: 750, color: "#0f172a" }}>{staff?.name || "Staff"}</span>
                                            <span style={{ display: "block", color: "#64748b", fontSize: "0.9rem" }}>{staff?.email || ""}{staff?.roleType ? ` · ${staff.roleType}` : ""}</span>
                                        </span>
                                        {hasRequirements ? (
                                            <Button
                                                type="button"
                                                icon="pi pi-pencil"
                                                rounded
                                                text
                                                severity="secondary"
                                                aria-label={`Edit ${staff?.name || "staff"}`}
                                                className="ml-auto"
                                                onClick={() => {
                                                    setStaffEditId(staffId);
                                                    setShowStaffSelectionDialog(false);
                                                    setShowManageStaffDialog(true);
                                                }}
                                            />
                                        ) : (
                                            <Button
                                                type="button"
                                                label="Set credentials"
                                                size="small"
                                                severity="danger"
                                                outlined
                                                rounded
                                                className="ml-auto"
                                                onClick={() => {
                                                    setStaffEditId(staffId);
                                                    setShowStaffSelectionDialog(false);
                                                    setShowManageStaffDialog(true);
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
                <div className="flex justify-content-end gap-2 flex-wrap" style={{ marginTop: "1.25rem" }}>
                    <Button type="button" label="Cancel" severity="secondary" rounded outlined onClick={() => setShowStaffSelectionDialog(false)} disabled={loadingStaffOptions} />
                    <Button type="button" label="Continue" icon="pi pi-arrow-right" severity="warning" rounded onClick={continueStaffSelection} disabled={loadingStaffOptions} />
                </div>
            </Dialog>
            <Dialog
                header="Select Assessment Version"
                visible={showAssessmentVersionDialog}
                style={{ width: "min(36rem, 94vw)" }}
                onHide={closeAssessmentVersionDialog}
                dismissableMask={!loadingAssessmentVersions && !importingMedicationSource}
                closable={!loadingAssessmentVersions && !importingMedicationSource}
            >
                <div style={{ color: "#475569", lineHeight: 1.7, marginBottom: "1rem" }}>
                    Choose which assessment version should supply the medication list before drafting delegation forms.
                </div>
                {loadingAssessmentVersions ? (
                    <div className="flex justify-content-center" style={{ padding: "1rem 0" }}>
                        <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="4" />
                    </div>
                ) : (
                    <>
                        {currentAssessmentVersionFormId ? (
                            <div style={{ marginBottom: "0.9rem", padding: "0.8rem 0.95rem", borderRadius: "12px", border: "1px solid #fdba74", background: "#fff7ed", color: "#9a3412", fontWeight: 700, lineHeight: 1.6 }}>
                                Current Version: {getCurrentAssessmentVersionSummary()}
                            </div>
                        ) : null}
                        <label htmlFor="assessment-version-selector" style={{ display: "block", fontWeight: 700, color: "#334155", marginBottom: "0.45rem" }}>
                            Assessment Version
                        </label>
                        <Dropdown
                            id="assessment-version-selector"
                            value={selectedAssessmentVersionFormId}
                            options={assessmentVersionOptions.map((form: any) => ({
                                label: `Version ${form?.version || 1}${form?.modifiedAt || form?.updatedAt || form?.createdAt ? ` • ${new Date(form?.modifiedAt || form?.updatedAt || form?.createdAt).toLocaleString()}` : ""}`,
                                value: String(form?.formId || "").trim(),
                            }))}
                            onChange={(e) => setSelectedAssessmentVersionFormId(String(e.value || "").trim())}
                            placeholder="Choose an assessment version"
                            style={{ width: "100%" }}
                            emptyMessage="No assessment versions available."
                        />
                    </>
                )}
                <div className="flex justify-content-end gap-2 flex-wrap" style={{ marginTop: "1.25rem" }}>
                    <Button
                        type="button"
                        label="Cancel"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={closeAssessmentVersionDialog}
                        disabled={loadingAssessmentVersions || importingMedicationSource}
                    />
                    <Button
                        type="button"
                        label="Continue"
                        icon="pi pi-arrow-right"
                        severity="warning"
                        rounded
                        onClick={() => {
                            setShowAssessmentVersionDialog(false);
                            void openAssessmentMedicationDraftPreview();
                        }}
                        disabled={!selectedAssessmentVersionFormId || loadingAssessmentVersions || importingMedicationSource}
                        loading={importingMedicationSource}
                    />
                </div>
            </Dialog>
            <Dialog
                header="Select Negotiated Care Plan Version"
                visible={showNcpVersionDialog}
                style={{ width: "min(36rem, 94vw)" }}
                onHide={closeNcpVersionDialog}
                dismissableMask={!loadingNcpVersions && !importingMedicationSource}
                closable={!loadingNcpVersions && !importingMedicationSource}
            >
                <div style={{ color: "#475569", lineHeight: 1.7, marginBottom: "1rem" }}>
                    Choose which negotiated care plan version should supply the medication list before drafting delegation forms.
                </div>
                {loadingNcpVersions ? (
                    <div className="flex justify-content-center" style={{ padding: "1rem 0" }}>
                        <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="4" />
                    </div>
                ) : (
                    <>
                        {currentNcpVersionFormId ? (
                            <div style={{ marginBottom: "0.9rem", padding: "0.8rem 0.95rem", borderRadius: "12px", border: "1px solid #fdba74", background: "#fff7ed", color: "#9a3412", fontWeight: 700, lineHeight: 1.6 }}>
                                Current Version: {getCurrentNcpVersionSummary()}
                            </div>
                        ) : null}
                        <label htmlFor="ncp-version-selector" style={{ display: "block", fontWeight: 700, color: "#334155", marginBottom: "0.45rem" }}>
                            Negotiated Care Plan Version
                        </label>
                        <Dropdown
                            id="ncp-version-selector"
                            value={selectedNcpVersionFormId}
                            options={ncpVersionOptions.map((form: any) => ({
                                label: `Version ${form?.version || 1}${form?.modifiedAt || form?.updatedAt || form?.createdAt ? ` • ${new Date(form?.modifiedAt || form?.updatedAt || form?.createdAt).toLocaleString()}` : ""}`,
                                value: String(form?.formId || "").trim(),
                            }))}
                            onChange={(e) => setSelectedNcpVersionFormId(String(e.value || "").trim())}
                            placeholder="Choose a negotiated care plan version"
                            style={{ width: "100%" }}
                            emptyMessage="No negotiated care plan versions available."
                        />
                    </>
                )}
                <div className="flex justify-content-end gap-2 flex-wrap" style={{ marginTop: "1.25rem" }}>
                    <Button
                        type="button"
                        label="Cancel"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={closeNcpVersionDialog}
                        disabled={loadingNcpVersions || importingMedicationSource}
                    />
                    <Button
                        type="button"
                        label="Continue"
                        icon="pi pi-arrow-right"
                        severity="warning"
                        rounded
                        onClick={() => {
                            setShowNcpVersionDialog(false);
                            void openNegotiatedCarePlanMedicationDraftPreview();
                        }}
                        disabled={!selectedNcpVersionFormId || loadingNcpVersions || importingMedicationSource}
                        loading={importingMedicationSource}
                    />
                </div>
            </Dialog>
            <Dialog
                header={medicationSourcePreviewTitle}
                visible={showMedicationSourcePreviewDialog}
                style={{ width: "min(58rem, 96vw)" }}
                onHide={closeMedicationSourcePreviewDialog}
            >
                <div style={{ color: "#475569", lineHeight: 1.7, marginBottom: "1rem" }}>
                    This will let the backend choose and create the best matching delegation draft forms from the medications shown below.
                </div>
                <div className="flex gap-2 flex-wrap" style={{ marginBottom: "1rem" }}>
                    <Tag value={`${medicationSourcePreviewEntries.length} medication${medicationSourcePreviewEntries.length === 1 ? "" : "s"}`} severity="info" rounded />
                    <Tag value="Multi-form draft selection" severity="warning" rounded />
                </div>
                {medicationSourcePreviewWarnings.length > 0 ? (
                    <div style={{ marginBottom: "1rem", padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #fde68a", background: "#fffbeb" }}>
                        <div style={{ fontWeight: 700, color: "#92400e", marginBottom: "0.35rem" }}>Parsing notes</div>
                        <div style={{ color: "#78350f", lineHeight: 1.65 }}>
                            {medicationSourcePreviewWarnings.join(" ")}
                        </div>
                    </div>
                ) : null}
                <div style={{ maxHeight: "22rem", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "14px", background: "#fff" }}>
                    {medicationSourcePreviewEntries.map((item: any, index: number) => (
                        <div
                            key={`${item?.medicationName || "medication"}-${index}`}
                            style={{
                                padding: "0.9rem 1rem",
                                borderBottom: index === medicationSourcePreviewEntries.length - 1 ? "none" : "1px solid #eef2f7",
                            }}
                        >
                            <div className="flex align-items-center gap-2 flex-wrap" style={{ marginBottom: "0.35rem" }}>
                                <div style={{ fontWeight: 700, color: "#0f172a" }}>{item?.medicationName || "Medication"}</div>
                                {item?.prn ? <Tag value="PRN" severity="warning" rounded /> : null}
                            </div>
                            <div style={{ color: "#475569", fontSize: "0.92rem", lineHeight: 1.6 }}>
                                {[item?.dosage, item?.route].filter(Boolean).join(" • ") || "No dose or route details"}
                            </div>
                            {item?.indication || item?.instructions ? (
                                <div style={{ color: "#64748b", fontSize: "0.85rem", lineHeight: 1.6, marginTop: "0.25rem", whiteSpace: "pre-wrap" }}>
                                    {[item?.indication, item?.instructions].filter(Boolean).join(" | ")}
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
                {medicationSourceDiagnosisText ? (
                    <div style={{ marginTop: "1rem", padding: "0.9rem 1rem", borderRadius: "14px", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.35rem" }}>Medication Diagnosis</div>
                        <div style={{ color: "#475569", lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
                            {medicationSourceDiagnosisText}
                        </div>
                    </div>
                ) : null}
                <div className="flex justify-content-end gap-2 flex-wrap" style={{ marginTop: "1.25rem" }}>
                    <Button
                        type="button"
                        label="Cancel"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={closeMedicationSourcePreviewDialog}
                        disabled={importingMedicationSource}
                    />
                    <Button
                        type="button"
                        label="Create Delegation Drafts"
                        severity="warning"
                        rounded
                        onClick={() => {
                            void createDelegationDraftsFromMedicationSource(
                                medicationSourceType === "ncp" ? "ncp" : "carePlan",
                                medicationSourcePreviewEntries,
                                {
                                    sourceLabel: medicationSourceType === "ncp" ? "Negotiated Care Plan" : "Manage Resident Assessment",
                                    assessmentFormId: selectedAssessmentVersionFormId || undefined,
                                    ncpFormId: selectedNcpVersionFormId || undefined,
                                },
                            );
                        }}
                        loading={importingMedicationSource}
                    />
                </div>
            </Dialog>
            <Dialog
                visible={showCarePlanRequiredDialog}
                style={{ width: "min(48rem, 94vw)" }}
                modal
                onHide={closeCarePlanRequiredDialog}
                header="Medication List Required"
            >
                <div style={{ color: "#475569", lineHeight: 1.7 }}>
                    No medication entries were found in {missingMedicationSourceLabel}. Update the medication list first, then try generating delegation drafts again.
                </div>
                <div className="flex justify-content-end" style={{ marginTop: "1.25rem" }}>
                    <Button
                        type="button"
                        label="Close"
                        severity="secondary"
                        rounded
                        outlined
                        onClick={closeCarePlanRequiredDialog}
                    />
                </div>
            </Dialog>
            <Dialog
                header={renderSurfaceHeader("More Access Needed", "", "pi pi-wallet")}
                visible={showFeatureAccessDialog}
                onHide={() => {
                    setShowFeatureAccessDialog(false);
                    setFeatureAccessDialogAllowsCreditUse(false);
                    delegationFeatureAccessRetryActionRef.current = null;
                    setImportingMedicationSource(false);
                }}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                modal
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                        {featureAccessDialogMessage}
                    </div>
                    {!requiresProviderSubscriptionMessageOnly ? (
                        <>
                            <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fed7aa", borderRadius: "14px", padding: "1rem" }}>
                                <div style={{ fontWeight: 700, color: "#9a3412" }}>Next</div>
                                <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                                    {featureAccessDialogAllowsCreditUse
                                        ? `You currently have ${automationCreditsAvailable} automation credit${automationCreditsAvailable === 1 ? "" : "s"} available. Use 1 credit to continue now.`
                                        : "Buy automation credits in Billing."}
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-content-end gap-2">
                                {featureAccessDialogAllowsCreditUse ? (
                                    <Button
                                        type="button"
                                        label="Use 1 Credit"
                                        severity="warning"
                                        rounded
                                        outlined
                                        onClick={() => {
                                            const retryAction = delegationFeatureAccessRetryActionRef.current;
                                            setShowFeatureAccessDialog(false);
                                            setFeatureAccessDialogAllowsCreditUse(false);
                                            delegationFeatureAccessRetryActionRef.current = null;
                                            retryAction?.();
                                        }}
                                    />
                                ) : (
                                    <Button
                                        type="button"
                                        label="Buy Automation Credits"
                                        severity="warning"
                                        rounded
                                        onClick={() => {
                                            setShowFeatureAccessDialog(false);
                                            navigate(`${RouterAddress.BILLING}?tab=credits`);
                                        }}
                                    />
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </Dialog>
            <Dialog
                visible={showDelegationDraftReuseDialog}
                header={renderSurfaceHeader("Draft Already Exists", "Delegation drafts were already created from this same source version.", "pi pi-copy")}
                style={appDialogStyle}
                headerStyle={appDialogHeaderStyle}
                contentStyle={appDialogContentStyle}
                modal
                onHide={resetDelegationDraftReuseState}
            >
                <div className="flex flex-column gap-3">
                    <div style={{ color: "#596F80", lineHeight: 1.7 }}>
                        Delegation drafts have already been created from this version. If you continue, AFH Hub will generate a fresh new set from the same source version.
                    </div>
                    {delegationDraftReuseForms.length ? (
                        <div style={{ ...appAccentPanelStyle, padding: "1rem" }}>
                            <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.55rem" }}>Existing Drafts</div>
                            {delegationDraftReuseForms.map((form: any, index: number) => (
                                <div
                                    key={form.formId || `${form.formCode}-${index}`}
                                    style={{
                                        padding: index === 0 ? "0 0 0.7rem" : "0.7rem 0",
                                        borderBottom: index === delegationDraftReuseForms.length - 1 ? "none" : "1px solid #fed7aa",
                                        fontWeight: 700,
                                        color: "#0f172a",
                                    }}
                                >
                                    {form.formCode} - {form.formTitle || "Delegation Form"}
                                </div>
                            ))}
                        </div>
                    ) : null}
                    <div className="flex justify-content-end gap-2 flex-wrap">
                        <Button
                            type="button"
                            label="Close"
                            severity="secondary"
                            rounded
                            outlined
                            onClick={resetDelegationDraftReuseState}
                        />
                        <Button
                            type="button"
                            label="Regenerate Drafts"
                            severity="warning"
                            rounded
                            onClick={() => {
                                void handleRegenerateDelegationDrafts();
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                visible={delegationDraftJobDialogVisible}
                style={{ width: "min(52rem, 96vw)" }}
                modal
                closable
                onHide={() => {
                    setDelegationDraftJobDialogVisible(false);
                    setDelegationDraftJobId("");
                    setDelegationDraftJobStatus("");
                    setDelegationDraftJobSubStatus("");
                    setDelegationDraftGeneratedForms([]);
                    setDelegationDraftResultMessage("");
                    setDelegationDraftErrorMessage("");
                    setDelegationDraftElapsedSeconds(0);
                }}
                header="Generating Delegation Drafts"
            >
                {String(delegationDraftJobStatus || "").toUpperCase() === "COMPLETED" ? (
                    <div className="flex flex-column gap-3" style={{ padding: "0.15rem 0.1rem 0.35rem" }}>
                        <div style={{
                            background: "#d1fae5",
                            border: "1.5px solid #34d399",
                            borderRadius: "10px",
                            padding: "0.9rem 1.1rem",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.7rem",
                        }}>
                            <i className="pi pi-check-circle" style={{ color: "#059669", fontSize: "1.3rem", marginTop: "0.1rem", flexShrink: 0 }} />
                            <div>
                                <div style={{ fontWeight: 700, color: "#065f46", fontSize: "0.95rem", marginBottom: "0.2rem" }}>
                                    Delegation Drafts Are Ready
                                </div>
                                <div style={{ color: "#047857", fontSize: "0.88rem" }}>
                                    {delegationDraftResultMessage || `${delegationDraftGeneratedForms.length} delegation draft form${delegationDraftGeneratedForms.length === 1 ? "" : "s"} created.`}
                                </div>
                            </div>
                        </div>
                        {delegationDraftGeneratedForms.length ? (
                            <div className="flex flex-column gap-2">
                                {delegationDraftGeneratedForms.map((form: any) => (
                                    <div
                                        key={form.formId || `${form.formCode}-${form.version}`}
                                        style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "0.95rem 1rem", background: "#fff" }}
                                    >
                                        <div style={{ minWidth: 0, fontSize: 0, lineHeight: 0 }}>
                                            <div style={{ fontSize: "1rem", fontWeight: 800, lineHeight: 1.5, color: "#0f172a" }}>
                                                {form.formCode} - {form.formDisplayTitle || form.formTitle || "Delegation Form"}
                                            </div>
                                            {form.reason ? ` • ${form.reason}` : ""}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{ color: "#64748b", fontWeight: 500 }}>
                                No supported delegation draft forms were created from this source.
                            </div>
                        )}
                        <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: "0.9rem", color: "#64748b", lineHeight: 1.65 }}>
                            <i style={{ color: "orange" }}>{AI_MESSAGE_FOR_COMPLIANCE}</i>
                        </div>
                    </div>
                ) : delegationDraftErrorMessage ? (
                    <div className="flex flex-column gap-3">
                        <div style={{ padding: "0.95rem 1rem", borderRadius: 14, border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", lineHeight: 1.7 }}>
                            {delegationDraftErrorMessage}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-column align-items-center text-center" style={{ padding: "0.4rem 0.2rem 0.6rem" }}>
                        <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="4" />
                        <div style={{ color: "#64748b", marginTop: "0.6rem" }}>
                            Current status: {delegationDraftJobStatus || "Queued"}
                        </div>
                        <div style={{ color: "#64748b", marginTop: "0.35rem" }}>
                            Elapsed time: {delegationDraftElapsedSeconds} second{delegationDraftElapsedSeconds === 1 ? "" : "s"}
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default NurseResidentDelegationPage;
